/**
 * Which catalog work may be sent to which robot.
 *
 * Combo missions, scenarios, and tasks may run on an Omni robot when every
 * station lot in the scenario is allowed for that robot. The allow-list is
 * `WASHING_LOT_ROBOT_TYPES` in stationLotModel (not a single dock SKU).
 * Work created for Omni stays on Omni. Changing a mission's own robot type
 * is a separate editor action.
 */

import { isWashingLotCompatibleWithRobotType, lotModelProfileId } from './stationLotModel'
import { VENDOR } from './vendorDisplayName'
import { getRobotTypeDisplayName } from './robotTypeDisplayName'

export const ROBOT_TYPE_COMBO = 'Combo'
export const ROBOT_TYPE_COMBO_OMNI = 'ComboOmni'

const AUTONOMICS_ROBOT_TYPES = new Set(['Sweeper', ROBOT_TYPE_COMBO, ROBOT_TYPE_COMBO_OMNI])

export type ScenarioMaintenanceLike = {
  service_points?: Array<{ kind?: string | null; lot_model?: string | null }> | null
} | null

function robotTypeLabel(code?: string | null): string {
  const trimmed = (code || '').trim()
  return getRobotTypeDisplayName(VENDOR.AUTONOMICS, trimmed) || trimmed
}

/** Catalog types that may be sent to this robot. Combo is included for Omni. */
export function entityRobotTypesForDispatchTarget(robotType?: string | null): string[] | undefined {
  const code = (robotType || '').trim()
  if (!code) return undefined
  if (code === ROBOT_TYPE_COMBO_OMNI) return [ROBOT_TYPE_COMBO_OMNI, ROBOT_TYPE_COMBO]
  return [code]
}

/** True when work created for `entityType` may be sent to `robotType` (types only). */
export function canDispatchEntityTypeToRobot(
  entityType?: string | null,
  robotType?: string | null
): boolean {
  const entity = (entityType || '').trim()
  const robot = (robotType || '').trim()
  if (!entity || !robot) return true
  if (!AUTONOMICS_ROBOT_TYPES.has(entity) || !AUTONOMICS_ROBOT_TYPES.has(robot)) {
    return entity === robot
  }
  const allowed = entityRobotTypesForDispatchTarget(robot) || []
  return allowed.includes(entity)
}

/** Station SKUs in the scenario that `WASHING_LOT_ROBOT_TYPES` denies for this robot. */
export function incompatibleStationLotModels(
  maintenance: ScenarioMaintenanceLike | undefined,
  robotType?: string | null
): string[] {
  const code = (robotType || '').trim()
  if (!code) return []
  const points = maintenance?.service_points
  if (!Array.isArray(points)) return []
  const seen = new Set<string>()
  const found: string[] = []
  for (const step of points) {
    if (!step) continue
    if (step.kind && step.kind !== 'station_lot') continue
    const lotModel = (step.lot_model || '').trim()
    if (!lotModel) continue
    if (isWashingLotCompatibleWithRobotType(lotModel, code)) continue
    const label = lotModelProfileId(lotModel) || lotModel
    if (seen.has(label)) continue
    seen.add(label)
    found.push(label)
  }
  return found
}

export function scenarioCompositionCompatibleWithRobot(
  maintenance: ScenarioMaintenanceLike | undefined,
  robotType?: string | null
): boolean {
  return incompatibleStationLotModels(maintenance, robotType).length === 0
}

export function canDispatchWorkToRobot(args: {
  entityType?: string | null
  robotType?: string | null
  maintenance?: ScenarioMaintenanceLike
}): boolean {
  if (!canDispatchEntityTypeToRobot(args.entityType, args.robotType)) return false
  return scenarioCompositionCompatibleWithRobot(args.maintenance, args.robotType)
}

/** Why this pair cannot be sent, or undefined when it can. */
export function dispatchBlockReason(args: {
  entityType?: string | null
  robotType?: string | null
  maintenance?: ScenarioMaintenanceLike
}): string | undefined {
  const entity = (args.entityType || '').trim()
  const robot = (args.robotType || '').trim()
  if (!entity || !robot) return undefined
  if (!canDispatchEntityTypeToRobot(entity, robot)) {
    return `Work created for ${robotTypeLabel(entity)} cannot be sent to ${robotTypeLabel(robot)}.`
  }
  const stations = incompatibleStationLotModels(args.maintenance, robot)
  if (stations.length === 0) return undefined
  if (stations.length === 1) {
    return `Station ${stations[0]} is not compatible with robot type ${robotTypeLabel(robot)}.`
  }
  return `Stations ${stations.join(', ')} are not compatible with robot type ${robotTypeLabel(robot)}.`
}

/**
 * Robots that may run this mission or scenario.
 * Undefined when the entity has no robot type (show every robot).
 */
export function robotTypesAcceptingWork(
  entityType?: string | null,
  maintenance?: ScenarioMaintenanceLike
): string[] | undefined {
  const entity = (entityType || '').trim()
  if (!entity) return undefined
  const candidates =
    entity === ROBOT_TYPE_COMBO ? [ROBOT_TYPE_COMBO, ROBOT_TYPE_COMBO_OMNI] : [entity]
  return candidates.filter(robotType =>
    canDispatchWorkToRobot({ entityType: entity, robotType, maintenance })
  )
}
